import type { AIProfile, ContinentId, GaraimonInstance, PlayerProfile, StyleId } from './types';
import { creaturesOn, getCreature } from './roster';
import { instinctWindow, styleProfiles, type StyleTraits } from './styles';

/**
 * Bumped when a change alters the arena a saved episode was played in, so a
 * stored episode can be told apart from one played under different rules.
 *
 * 0.3.0: obstacles are drawn from sprite art with a footprint smaller than the
 *        image, so the collision rectangles no longer match 0.2.0 for a seed.
 *        Terrain ids were also renamed (thicket to tree, stone to rock,
 *        crystal to structure), so an arena fingerprint recorded under the old
 *        names no longer matches one recorded under the new ones.
 * 0.4.0: the arena is no longer derived from terrain the player placed. It is
 *        rolled from its own seed, and its obstacle layout is only accepted once
 *        a walking route between the two creatures has been found. A 0.4.0 arena
 *        is therefore rolled rather than placed, which its fingerprint also
 *        shows, and its floor can differ from a 0.3.0 arena of the same seed.
 *        See `domain/arena.ts` for why the route guarantee exists.
 */
export const SIMULATION_VERSION = '0.4.0';
/** Decision logic changed when the Brawler style was added, so replays must be distinguishable. */
export const POLICY_VERSION = 'utility-3';
/** Bumped because Kitsprig was replaced by Kameboom, and again when Fawnlet became Cluckoo. */
export const SAVE_KEY = 'garaimon.save.v3';

/**
 * The species granted to a new player while the game is in development.
 *
 * Two entries rather than one, so the player starts with a genuine choice of
 * which creature to summon. They are the two species with real animated art, so
 * the development build shows the whole art pipeline without waiting for either
 * one to turn up as a wild encounter. Both are owned from the first battle, and
 * the in-battle roster panel switches between them at any point.
 *
 * Real onboarding will replace this with a genuine choice made by the player.
 * When it does, this list is the only thing that changes; the grant step does not.
 */
export const DEV_STARTER_LOADOUT = ['asia-kameboom', 'europe-cluckoo'] as const;

/**
 * Fixed seed for the development starter, so development battles are
 * reproducible. Each starter in the loadout takes the next value, so every
 * creature still gets a stable, distinct seed.
 */
const DEV_STARTER_SEED = 0x73746172;

export const blankProfile = (): PlayerProfile => ({
  gachaCards: 0, noCardStreak: 0, garaimon: [], recentBoardSeeds: [], episodes: [], continent: 'asia',
});

export function loadProfile(): PlayerProfile {
  try {
    const data = localStorage.getItem(SAVE_KEY);
    if (!data) return blankProfile();
    const parsed = JSON.parse(data) as PlayerProfile;
    // The recent-seed list is rebuilt rather than migrated: it only avoids
    // repeating a board the player has just seen, so an older save's patterns are
    // not worth keeping and there is nothing for a missing list to break.
    return { ...blankProfile(), ...parsed, garaimon: parsed.garaimon ?? [], episodes: parsed.episodes ?? [], recentBoardSeeds: parsed.recentBoardSeeds ?? [] };
  } catch {
    return blankProfile();
  }
}

export function saveProfile(profile: PlayerProfile): void {
  localStorage.setItem(SAVE_KEY, JSON.stringify(profile));
}

/**
 * Removes the local copy of a player's progress.
 *
 * Used only by sign-out, which is a confirmed product decision: signing out
 * clears the device's local copy so the next person on the device starts fresh.
 * See `docs/decisions/DEC-006-sign-out.md`.
 */
export function clearProfile(): void {
  localStorage.removeItem(SAVE_KEY);
  clearSaveSync();
}

/**
 * The account this device's save is known to have been written into.
 *
 * The device has one save slot and it is shared by everyone who plays on the device,
 * so the slot by itself cannot say whose progress it holds, or whether the account
 * already has it. This stamp is that record: the account id last confirmed to hold
 * exactly this copy.
 *
 * It is removed whenever a save to the account fails, so its absence means "this copy
 * has changes the account may not have". That is what makes a question avoidable when
 * there is nothing to ask about, and unavoidable when there is:
 *
 * - a stamp naming the account signing in means the two copies already agree, so
 *   there is nothing to choose between;
 * - a stamp naming a *different* account means the copy belongs to someone else, and
 *   it must never be offered up as this account's to overwrite;
 * - no stamp means unknown, which is treated as "the account may not have this".
 *
 * Failing to read or write the stamp is not fatal, and is deliberately biased the
 * same direction as everything else here: an unreadable stamp reads as unknown, which
 * asks the player rather than deciding for them.
 */
export const SAVE_SYNC_KEY = 'garaimon.save.sync';

export function loadSaveSync(): string | null {
  try {
    return localStorage.getItem(SAVE_SYNC_KEY);
  } catch {
    return null;
  }
}

export function markSaveSynced(accountId: string): void {
  try {
    localStorage.setItem(SAVE_SYNC_KEY, accountId);
  } catch (error) {
    // Only means the next sign-in will ask again rather than assume. Worth saying out
    // loud, because a silent failure here would look like the question is broken.
    console.warn('Garaimon: the account sync stamp could not be written, so the next sign-in will ask again.', error);
  }
}

export function clearSaveSync(): void {
  try {
    localStorage.removeItem(SAVE_SYNC_KEY);
  } catch {
    /* Nothing to recover: an unremovable stamp reads as unknown at worst. */
  }
}

export function seededRandom(seed: number): () => number {
  let value = seed >>> 0;
  return () => {
    value += 0x6d2b79f5;
    let t = value;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function createSeed(): number {
  return Math.floor(Math.random() * 2_147_483_647);
}

export function makeAIProfile(style: StyleId, seed: number): AIProfile {
  const random = seededRandom(seed);
  const traits = styleProfiles[style].traits;
  const vary = (n: number) => Math.max(0.05, Math.min(0.98, n + (random() - 0.5) * 0.22));
  return { style, aggression: vary(traits.aggression), caution: vary(traits.caution), curiosity: vary(traits.curiosity), patience: vary(traits.patience), confidence: vary(traits.confidence), observations: 0, policyVersion: POLICY_VERSION };
}

/**
 * The reward for a player's first completed battle.
 *
 * There is no onboarding gift card, so a roster that starts with none would have
 * nothing to catch with. The first battle always pays exactly this many, which is
 * independent of the roll below and mirrored by the same rule in the server's
 * `finalize_battle`.
 */
export const FIRST_BATTLE_CARD_REWARD = 1;

export function rollCardReward(profile: PlayerProfile, random = Math.random): { cards: number; streak: number } {
  if (profile.noCardStreak >= 2) return { cards: 1, streak: 0 };
  const roll = random();
  if (roll < 0.2) return { cards: 0, streak: profile.noCardStreak + 1 };
  if (roll < 0.9) return { cards: 1, streak: 0 };
  return { cards: 2, streak: 0 };
}

export function newGaraimon(speciesId: string, seed: number): GaraimonInstance {
  const creature = getCreature(speciesId);
  const id = crypto.randomUUID();
  return {
    id,
    speciesId,
    nickname: '',
    level: 1,
    xp: 0,
    learnedAbilityIds: creature.abilities.map((ability) => ability.id),
    aiProfile: makeAIProfile(creature.style, seed),
  };
}

/**
 * Grants the development starters to a player who has no Garaimon yet, and
 * returns the first one so the caller can persist the change.
 *
 * Every species in `DEV_STARTER_LOADOUT` is granted, so the player owns a real
 * choice from their first battle. This is an explicit step with a name rather
 * than a side effect buried inside battle start-up, so the roster a player
 * begins with is predictable.
 */
export function grantStarterGaraimon(profile: PlayerProfile, continent: ContinentId, excludeSpeciesId: string): GaraimonInstance | undefined {
  if (profile.garaimon.length > 0) return undefined;
  const granted: GaraimonInstance[] = [];
  for (const [index, speciesId] of DEV_STARTER_LOADOUT.entries()) {
    const species = getCreature(speciesId);
    let chosen = species;
    if (species.id === excludeSpeciesId) {
      // The player is already facing this species as the wild encounter, so it
      // cannot also be the gift. Swap in another creature from that continent.
      // `creaturesOn` is continent-scoped, so the replacement cannot collide with
      // another starter from a different continent.
      const alternative = creaturesOn(continent).find((creature) => creature.id !== excludeSpeciesId);
      if (!alternative) throw new Error(`Garaimon: no starter is available on ${continent} beside "${excludeSpeciesId}".`);
      chosen = alternative;
    }
    if (granted.some((instance) => instance.speciesId === chosen.id)) {
      throw new Error(`Garaimon: the development starter loadout would grant "${chosen.id}" twice.`);
    }
    const starter = newGaraimon(chosen.id, DEV_STARTER_SEED + index);
    profile.garaimon.push(starter);
    granted.push(starter);
  }
  return granted[0];
}

export function trainProfile(profile: AIProfile, episode: { won: boolean; damageDealt: number; damageReceived: number; abilityUses: number; blocked: number }): { profile: AIProfile; note: string } {
  const next = { ...profile, observations: profile.observations + 1 };
  // Instincts move only within a fixed window around the style's baseline, so
  // repeated wins or losses cannot walk an archetype into a different one.
  const shift = (trait: keyof StyleTraits, amount: number) => {
    const { low, high } = instinctWindow(profile.style, trait);
    return Math.max(low, Math.min(high, next[trait] + amount));
  };
  const notes: string[] = [];
  if (!episode.won && episode.damageReceived > episode.damageDealt) {
    next.caution = shift('caution', 0.018);
    next.patience = shift('patience', 0.01);
    notes.push('has started to watch for danger');
  } else if (episode.won && episode.damageDealt > episode.damageReceived) {
    next.confidence = shift('confidence', 0.014);
    notes.push('is growing more confident');
  }
  if (episode.blocked > 1) {
    next.curiosity = shift('curiosity', 0.012);
    notes.push('is learning to look for clearer paths');
  }
  if (episode.abilityUses > 3) next.patience = shift('patience', 0.006);
  return { profile: next, note: notes[0] ?? 'is settling into its own rhythm' };
}

export function createStartingOpponent(continent: ContinentId, ownedSpecies: string[], seed: number) {
  const candidates = creaturesOn(continent).filter((creature) => !ownedSpecies.includes(creature.id));
  const set = candidates.length ? candidates : creaturesOn(continent);
  const random = seededRandom(seed);
  const species = set[Math.floor(random() * set.length)]!;
  return newGaraimon(species.id, seed ^ 0x51f15e);
}
